import {
  CollapsedWarningPill,
  CollapsibleWarningBanner,
} from "@/components/navigation/CollapsibleWarningBanner";
import {
  DangerousEnvVarDescription,
  useHasDangerousEnvVars,
} from "@/utils/hooks/engine/use-has-dangerous-env-vars";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { Routes } from "@/utils/routes";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import React, { useMemo, useState } from "react";

const MAX_INITIAL_ITEMS = 1;

export interface DangerousEnvVarsBannerProps {
  collapsed?: boolean;
  onToggleCollapsed?: (collapsed: boolean) => void;
}

export const DangerousEnvVarsBanner: React.FC<DangerousEnvVarsBannerProps> = ({
  collapsed = false,
  onToggleCollapsed,
}) => {
  const { hasDangerousEnvVars, dangerousEnvVars } = useHasDangerousEnvVars();
  const { projectId, environmentId } = useRouteAndTeamInfo();
  const [showAll, setShowAll] = useState(false);

  const resourceLink = useMemo(() => {
    if (!projectId || !environmentId) {
      return undefined;
    }

    return Routes.project.environment.envVariables(projectId, environmentId);
  }, [projectId, environmentId]);

  const defaultItems = useMemo(() => {
    return dangerousEnvVars.slice(0, MAX_INITIAL_ITEMS);
  }, [dangerousEnvVars]);

  const restItems = useMemo(() => {
    return dangerousEnvVars.slice(MAX_INITIAL_ITEMS);
  }, [dangerousEnvVars]);

  if (!hasDangerousEnvVars) {
    return null;
  }

  if (collapsed) {
    return (
      <CollapsedWarningPill
        className="border-cds-status-orange-border bg-cds-status-orange-fill text-cds-status-orange-text hover:brightness-95 dark:border-cds-status-orange-border-dark dark:bg-cds-status-orange-fill-dark dark:text-cds-status-orange-text-dark dark:hover:brightness-110"
        label="Env var overrides"
        onClick={() => onToggleCollapsed?.(false)}
      />
    );
  }

  const renderOverrideItem = (varSpec: DangerousEnvVarDescription, idx: number) => {
    const locationInfo = varSpec.service
      ? varSpec.resourceGroup && varSpec.resourceGroup !== "default"
        ? ` (${varSpec.resourceGroup} / ${varSpec.service})`
        : ` (${varSpec.service})`
      : "";

    const content = (
      <>
        -{" "}
        <span className={"font-medium"}>
          {varSpec.name}={varSpec.value}
          {locationInfo} -
        </span>{" "}
        <span>{varSpec.description}</span>
      </>
    );

    return (
      <li key={idx}>
        {resourceLink ? (
          <ChalkLink href={resourceLink} className="hover:underline">
            {content}
          </ChalkLink>
        ) : (
          content
        )}
      </li>
    );
  };

  return (
    <CollapsibleWarningBanner
      className="bg-cds-status-orange-fill text-cds-status-orange-text dark:bg-cds-status-orange-fill-dark dark:text-cds-status-orange-text-dark"
      onCollapse={onToggleCollapsed ? () => onToggleCollapsed(true) : undefined}
      title={
        <>
          NOTE: This environment has environment variables with possibly dangerous values
          configured.
        </>
      }
    >
      {dangerousEnvVars.length > 0 && (
        <ul className="mt-1 text-xs space-y-0.5">
          {defaultItems.map((override, idx) => renderOverrideItem(override, idx))}
          {showAll &&
            restItems.map((override, idx) => renderOverrideItem(override, idx + MAX_INITIAL_ITEMS))}
          {restItems.length > 0 && (
            <li>
              <button
                onClick={() => setShowAll(!showAll)}
                className="hover:underline cursor-pointer"
              >
                {showAll ? `- Show less` : `+ ${restItems.length} more`}
              </button>
            </li>
          )}
        </ul>
      )}
    </CollapsibleWarningBanner>
  );
};
